import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getAuctionRestoreBudgetMinValue } from 'utils/auction';
import { formatCostInCents, getNullableNumberFromString, getStringFromNullableNumber } from 'utils/clickme';
import {
    BUDGET_MAX_LENGTH,
    BUDGET_STEP,
    MAXIMAL_BUDGET_IN_CENTS,
    MINIMAL_BUDGET_IN_CENTS,
    OPTIMAL_BUDGET_IN_CENTS,
} from 'utils/constants/auction';

import auctionCampaignBudgetFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_budget_form_field_touch';
import auctionCampaignBudgetPlusMinusButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_budget_plus_minus_button_click';
import auctionCampaignBudgetPresetButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_budget_preset_button_click';
import auctionCampaignManualBudgetButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_manual_budget_button_click';
import { Link, Text, useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';

import { formatToReactComponent } from 'bloko/common/trl';

import { useAuctionBidValidation } from 'hooks/auction/useAuctionBidValidation';
import { useAuctionBudgetValidation } from 'hooks/auction/useAuctionBudgetValidation';
import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';
import { useAvailableFirstCampaignAuctionPromo } from 'hooks/useAvailableFirstCampaignAuctionPromo';

import { AuctionBudgetCard } from 'components/AuctionBuilder/AuctionBudget/AuctionBudgetCard/AuctionBudgetCard';
import { AuctionTotal } from 'components/AuctionBuilder/AuctionBudget/AuctionTotal/AuctionTotal';
import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { MoneyInputWithSteps } from 'components/common/MoneyInputWithSteps/MoneyInputWithSteps';

import {
    AuctionBuilderBudgetErrorCode,
    auctionSelector,
    auctionSelectorNonNullable,
    setBudgetValue,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/AuctionBuilder/AuctionBudget/AuctionBudget.module.less';

export const enum BudgetPreset {
    Minimal = 'minimal',
    Optimal = 'optimal',
    Maximal = 'maximal',
}

const TrlKeys = {
    label: 'AuctionBuilder.AuctionBudget.label',
    labelTooltip: {
        part1: 'AuctionBuilder.AuctionBudget.labelTooltip.part1',
        part2: 'AuctionBuilder.AuctionBudget.labelTooltip.part2',
    },
    inputDescription: 'AuctionBuilder.AuctionBudget.inputDescription',
    budgetPreset: {
        [BudgetPreset.Minimal]: 'AuctionBuilder.AuctionBudget.budgetPreset.minimal',
        [BudgetPreset.Optimal]: 'AuctionBuilder.AuctionBudget.budgetPreset.optimal',
        [BudgetPreset.Maximal]: 'AuctionBuilder.AuctionBudget.budgetPreset.maximal',
    },
    switchToManual: 'AuctionBuilder.AuctionBudget.switchToManual',
    switchToPresets: 'AuctionBuilder.AuctionBudget.switchToPresets',
    error: {
        [AuctionBuilderBudgetErrorCode.RangeUnderflow]: 'AuctionBuilder.AuctionBudget.error.rangeUnderflow',
        [AuctionBuilderBudgetErrorCode.ValueMissing]: 'AuctionBuilder.AuctionBudget.error.valueMissing',
        [AuctionBuilderBudgetErrorCode.BudgetCanNotBeLessThanAlreadySpent]:
            'AuctionBuilder.AuctionBudget.error.budgetCanNotBeLessThanAlreadySpent',
        default: 'AuctionBuilder.AuctionBudget.error.default',
    },
};

const getErrorTrl = (errorCode: string): string => {
    return TrlKeys.error[errorCode as keyof typeof TrlKeys.error] || TrlKeys.error.default;
};

const roundToBudgetStep = (value: number) => {
    return Math.ceil(value / BUDGET_STEP) * BUDGET_STEP;
};

const getAuctionBudgetPresets = (bid: number) => ({
    [BudgetPreset.Minimal]: bid < 1000 ? MINIMAL_BUDGET_IN_CENTS : roundToBudgetStep(bid * 150),
    [BudgetPreset.Optimal]: bid < 1000 ? OPTIMAL_BUDGET_IN_CENTS : roundToBudgetStep(bid * 300),
    [BudgetPreset.Maximal]: bid < 1000 ? MAXIMAL_BUDGET_IN_CENTS : roundToBudgetStep(bid * 800),
});

export const AuctionBudget: FC = () => {
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);
    const {
        value: bidValue,
        initialValue: bidInitialValue,
        minValue: minBid,
    } = useSelector(auctionSelectorNonNullable.bid);
    const {
        value: budgetValue,
        minValue: minBudget,
        initialValue: budgetInitialValue,
    } = useSelector(auctionSelectorNonNullable.budget);
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;
    const errorCode = useSelector(auctionSelector.errors.budget);

    const [isManualMode, setIsManualModeOn, setIsManualModeOff] = useOnOffState(!!auctionCampaign);
    const [selectedBudgetPreset, setSelectedBudgetPreset] = useState(BudgetPreset.Optimal);
    const [budgetPresets, setBudgetPresets] = useState(getAuctionBudgetPresets(bidValue || bidInitialValue));

    const dispatch = useDispatch();
    const trls = useTrls();
    const { isXS } = useBreakpoint();
    const validateBudget = useAuctionBudgetValidation();
    const validateBid = useAuctionBidValidation();
    const { isPromoAvailable } = useAvailableFirstCampaignAuctionPromo();

    const restoreMinValue = useMemo(() => {
        if (!auctionCampaign) return budgetInitialValue;
        const bid = bidValue || bidInitialValue;
        return getAuctionRestoreBudgetMinValue(budgetInitialValue, auctionCampaign.campaignBalance, bid);
    }, [auctionCampaign, budgetInitialValue, bidValue, bidInitialValue]);

    const formattedMinValue =
        errorCode === AuctionBuilderBudgetErrorCode.BudgetCanNotBeLessThanAlreadySpent
            ? formatCostInCents(restoreMinValue)
            : formatCostInCents(minBudget);

    const errorText = errorCode ? trls(getErrorTrl(errorCode), [formattedMinValue]) : null;

    const onBudgetChange = useCallback(
        (value: string) => {
            const intValue = getNullableNumberFromString(value);
            dispatch(setBudgetValue(intValue));
            validateBudget(intValue);
            validateBid(bidValue, intValue);
        },
        [bidValue, dispatch, validateBid, validateBudget]
    );

    const onBudgetPresetChange = (budgetPresetType: BudgetPreset) => {
        onBudgetChange(String(budgetPresets[budgetPresetType]));
        setSelectedBudgetPreset(budgetPresetType);
        auctionCampaignBudgetPresetButtonClick({
            presetType: budgetPresetType,
            budgetValue: budgetPresets[budgetPresetType],
            vacancyId,
        });
    };

    const onSwitchToPresetsBudget = () => {
        setIsManualModeOff();
        setSelectedBudgetPreset(BudgetPreset.Optimal);
        onBudgetChange(String(budgetPresets[BudgetPreset.Optimal]));
    };

    const onSwitchToManualBudget = () => {
        setIsManualModeOn();
        auctionCampaignManualBudgetButtonClick({ vacancyId });
    };

    useEffect(() => {
        const auctionBudgetPresets = getAuctionBudgetPresets(bidValue || minBid);
        setBudgetPresets(auctionBudgetPresets);
        if (!isManualMode) {
            dispatch(setBudgetValue(auctionBudgetPresets[selectedBudgetPreset]));
        }
    }, [bidValue, dispatch, isManualMode, minBid, selectedBudgetPreset]);

    return (
        <>
            <MagritteFormField
                label={trls(TrlKeys.label)}
                labelTooltipContent={
                    <>
                        <Text typography="paragraph-2-regular">{trls(TrlKeys.labelTooltip.part1)}</Text>
                        <VSpacing default={8} />
                        <Text typography="paragraph-2-regular">{trls(TrlKeys.labelTooltip.part2)}</Text>
                    </>
                }
                input={
                    isManualMode ? (
                        <div className={styles.manualBudgetWrapper}>
                            <div className={styles.inputWrapper}>
                                <MoneyInputWithSteps
                                    dataQa="auction-budget"
                                    value={getStringFromNullableNumber(budgetValue)}
                                    step={BUDGET_STEP}
                                    maxLength={BUDGET_MAX_LENGTH}
                                    minValue={minBudget}
                                    invalid={!!errorText}
                                    errorMessage={errorText}
                                    description={
                                        auctionCampaign &&
                                        formatToReactComponent(trls(TrlKeys.inputDescription), {
                                            '[0]': formatCostInCents(
                                                budgetValue
                                                    ? budgetValue -
                                                          (auctionCampaign.budget - auctionCampaign.campaignBalance)
                                                    : auctionCampaign.campaignBalance
                                            ),
                                            '[1]': formatCostInCents(budgetValue || auctionCampaign.budget),
                                        })
                                    }
                                    onChange={onBudgetChange}
                                    onMinusClick={() =>
                                        auctionCampaignBudgetPlusMinusButtonClick({
                                            buttonName: 'auction_campaign_budget_minus',
                                            vacancyId,
                                            campaignId,
                                        })
                                    }
                                    onPlusClick={() =>
                                        auctionCampaignBudgetPlusMinusButtonClick({
                                            buttonName: 'auction_campaign_budget_plus',
                                            vacancyId,
                                            campaignId,
                                        })
                                    }
                                    onBlur={() =>
                                        auctionCampaignBudgetFormFieldTouch({
                                            value: String(budgetValue),
                                            vacancyId,
                                            campaignId,
                                        })
                                    }
                                />
                            </div>
                            {!isXS && <div className={styles.flexBlock}></div>}
                        </div>
                    ) : (
                        <div className={styles.presetsWrapper}>
                            {Object.entries(budgetPresets).map(([key, value]) => (
                                <AuctionBudgetCard
                                    key={key}
                                    amount={value}
                                    checked={key === selectedBudgetPreset}
                                    description={trls(TrlKeys.budgetPreset[key as BudgetPreset])}
                                    onClick={() => onBudgetPresetChange(key as BudgetPreset)}
                                    dataQa={`auction-budget-preset-${key}`}
                                />
                            ))}
                        </div>
                    )
                }
            />
            {!auctionCampaign && isPromoAvailable && (
                <>
                    <VSpacing default={12} />
                    <AuctionTotal />
                </>
            )}
            <VSpacing default={16} />
            {isManualMode ? (
                <>
                    {!auctionCampaign && (
                        <Link Element="button" data-qa="presets-mode-link" onClick={onSwitchToPresetsBudget}>
                            {trls(TrlKeys.switchToPresets)}
                        </Link>
                    )}
                </>
            ) : (
                <Link Element="button" data-qa="manual-mode-link" onClick={onSwitchToManualBudget}>
                    {trls(TrlKeys.switchToManual)}
                </Link>
            )}
        </>
    );
};
